import type { TFunction } from 'i18next';
import type { WalletUpcomingPayments } from '../../../../queries/pxtraCard';
import { formatCents, formatDayMonth, monthName } from './format';

export type WalletAlertModel = { severity: 'success' | 'error'; title: string; text: string };

/**
 * The banner for the period's state; the empty state has none. The escalation
 * states show the backend's day count and month, so the banner never disagrees
 * with the API about when the payments lapse. A backend that sends an escalation
 * state without those numbers gets the plain shortfall banner.
 */
export const walletAlertModel = (
  t: TFunction,
  upcoming: WalletUpcomingPayments,
  balanceCents: number
): WalletAlertModel | null => {
  const shortfall = formatCents(upcoming.shortfall_cents);
  const current = formatCents(upcoming.shortfall_current_month_cents);
  const { escalation_days_left: daysLeft, escalation_month: escalationMonth } = upcoming;
  const shortfallModel = (): WalletAlertModel => ({
    severity: 'error',
    title: t('pxtraCard:alertShortfallTitle', { amount: shortfall }),
    text: t('pxtraCard:alertShortfallText', { amount: shortfall })
  });

  switch (upcoming.state) {
    case 'funded':
      return {
        severity: 'success',
        title: t('pxtraCard:alertFundedTitle'),
        text: t('pxtraCard:alertFundedText', { balance: formatCents(balanceCents) })
      };
    case 'covered':
      return {
        severity: 'success',
        title: t('pxtraCard:alertCoveredTitle'),
        text: t('pxtraCard:alertCoveredText', { total: formatCents(upcoming.total_cents) })
      };
    case 'shortfall':
      return shortfallModel();
    case 'escalation_week':
      if (daysLeft === null || !escalationMonth || !upcoming.deadline) return shortfallModel();
      return {
        severity: 'error',
        title: t('pxtraCard:alertEscalationWeekTitle', { count: daysLeft }),
        text: t('pxtraCard:alertEscalationWeekText', {
          amount: current,
          date: formatDayMonth(upcoming.deadline),
          month: monthName(escalationMonth)
        })
      };
    case 'escalation_last_day':
      if (!escalationMonth) return shortfallModel();
      return {
        severity: 'error',
        title: t('pxtraCard:alertEscalationLastDayTitle'),
        text: t('pxtraCard:alertEscalationLastDayText', {
          amount: current,
          month: monthName(escalationMonth)
        })
      };
    default:
      return null;
  }
};
